import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import API_BASE from '../config/api';

export const WorkspaceContext = createContext(null);

export function WorkspaceProvider({ children }) {
  const { token } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspace, setActiveWorkspace] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);

  // Reload recent items whenever the active workspace changes
  useEffect(() => {
    if (!activeWorkspace?._id) {
      setRecentProjects([]);
      setRecentEvents([]);
      return;
    }
    try {
      setRecentProjects(
        JSON.parse(localStorage.getItem(`vance_recent_projects_${activeWorkspace._id}`)) || []
      );
    } catch {
      setRecentProjects([]);
    }
    try {
      setRecentEvents(
        JSON.parse(localStorage.getItem(`vance_recent_events_${activeWorkspace._id}`)) || []
      );
    } catch {
      setRecentEvents([]);
    }
  }, [activeWorkspace?._id]);

  async function fetchWorkspaces() {
    try {
      const res = await axios.get(`${API_BASE}/api/workspaces`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data;
      setWorkspaces(data);

      if (data.length > 0) {
        const savedId = localStorage.getItem('vance_active_workspace');
        const saved = savedId ? data.find((w) => w._id === savedId) : null;
        setActiveWorkspace(saved || data[0]);
      }
    } catch (err) {
      console.error('Failed to fetch workspaces:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function createWorkspace(name, color) {
    const res = await axios.post(
      `${API_BASE}/api/workspaces`,
      { name, color },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const created = res.data;
    setWorkspaces((prev) => [...prev, created]);
    setActiveWorkspace(created);
    localStorage.setItem('vance_active_workspace', created._id);
    return created;
  }

  function addRecentProject(project) {
    if (!activeWorkspace?._id) return;
    const key = `vance_recent_projects_${activeWorkspace._id}`;
    const entry = {
      _id: project._id,
      title: project.title,
      status: project.status,
      start_date: project.start_date,
      end_date: project.end_date,
    };
    const updated = [
      entry,
      ...recentProjects.filter((p) => p._id !== project._id),
    ].slice(0, 5);
    localStorage.setItem(key, JSON.stringify(updated));
    setRecentProjects(updated);
  }

  function addRecentEvent(event) {
    if (!activeWorkspace?._id) return;
    const key = `vance_recent_events_${activeWorkspace._id}`;
    const entry = {
      _id: event._id,
      title: event.title,
      status: event.status,
      start_date: event.start_date,
      end_date: event.end_date,
    };
    const updated = [
      entry,
      ...recentEvents.filter((e) => e._id !== event._id),
    ].slice(0, 5);
    localStorage.setItem(key, JSON.stringify(updated));
    setRecentEvents(updated);
  }

  function switchWorkspace(workspace) {
    setActiveWorkspace(workspace);
    localStorage.setItem('vance_active_workspace', workspace._id);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (token) {
      fetchWorkspaces();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  return (
    <WorkspaceContext.Provider
      value={{ workspaces, activeWorkspace, isLoading, fetchWorkspaces, createWorkspace, switchWorkspace, recentProjects, addRecentProject, recentEvents, addRecentEvent }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export const useWorkspace = () => useContext(WorkspaceContext);
